/**
 * searchPageMap.js — Step 10A: single source of truth for search-only pages.
 *
 * Each entry describes one search route.  Entries fall into two kinds:
 *
 *   surface.kind === 'redirect' — a compatibility alias that redirects to another
 *                                  canonical path.  Nav metadata fields are cleared
 *                                  (navGroup: null) because redirect aliases do not
 *                                  back real pages or appear in any nav derivation.
 *
 *   anything else               — a real navigable page.  All nav metadata fields
 *                                  are required and must be populated.
 *
 *   id                 — stable string identifier used by tests and utilities.
 *   path               — URL suffix after /app/:shopId  (e.g. "/home").
 *   linkKey            — key in linkBuilder.Links used to build resolved hrefs.
 *   menuTestId         — data-testid on the sidebar menu item (null = no menu entry).
 *   requiresCapability — ShellAppContext capabilities key that must be truthy.
 *                        null = page is always accessible (no capability check).
 *   legacyAliases      — old URL suffixes that redirect to `path` for backwards compat.
 *   surface            — describes the actual page chrome at runtime (see PageSurface).
 */

/**
 * @typedef {
 *   | { kind: 'feature_page_shell', width: 'default'|'wide'|'expanded'|'fluid' }
 *   | { kind: 'legacy_shell' }
 *   | { kind: 'unstyled' }
 *   | { kind: 'redirect' }
 * } PageSurface
 *
 * surface.kind values:
 *   'feature_page_shell' — page renders under FeaturePageShell; width is the prop passed
 *                          (or 'default' when omitted, per FeaturePageShell.defaultProps).
 *   'legacy_shell'       — page uses ContainerHeader + Widget legacy chrome; not yet migrated.
 *   'unstyled'           — page renders under a plain Box/Container/div with no admin shell.
 *   'redirect'           — this map entry is a router redirect alias; no page component renders.
 *
 * Epic 14 extension: when migrating a page onto FeaturePageShell, change kind to
 * 'feature_page_shell' and set width explicitly. The C16 tests in searchPageMap.test.js
 * will enforce the update.
 */

/**
 * @typedef {{
 *   id: string,
 *   path: string,
 *   linkKey: string,
 *   menuTestId: string|null,
 *   requiresCapability: string|null,
 *   legacyAliases: string[],
 *   surface: PageSurface,
 *   title: string,
 *   titleKey: string|null,
 *   headerOwner: 'shell'|'page'|null,
 *   navGroup: 'top'|'analytics'|'settings'|'merchandising'|'uiSettings'|null,
 *   cloudNavGroup?: 'top'|'analytics'|'settings'|'customization'|'merchandising'|null,
 *   cloudNavOrder?: number,
 *   hiddenInCloudNavigation?: boolean,
 *   hiddenInCloudHorizontalNav?: boolean,
 *   navOrder: number,
 *   navIcon: string|null,
 *   navName: string,
 *   productIds?: string[],
 * }} SearchPageEntry
 *
 * Two variants exist at runtime:
 *
 * Page entry (surface.kind !== 'redirect'):
 *   All fields are meaningful.  requiresCapability is the ShellAppContext capability
 *   that gates the route.  Nav metadata fields (title, navGroup, navOrder, navIcon,
 *   navName) are populated.  getPluginAdminNavEntries() includes this entry.
 *
 *   titleKey       — i18n key used to produce a localized page heading.
 *                    Non-null when headerOwner === 'shell'.
 *                    Null when headerOwner === 'page' (inner component owns its header).
 *   headerOwner    — 'shell': FeaturePageShell renders the <h1> via title={t(titleKey)}.
 *                    'page': inner component renders its own header; shell must NOT
 *                            receive a title prop.
 *
 * Redirect alias (surface.kind === 'redirect'):
 *   Models a router compatibility redirect only.  No real page renders.
 *   requiresCapability: null  — the redirect itself is ungated; the destination
 *                               page owns its own capability gate.
 *   menuTestId: null          — no sidebar item.
 *   navGroup: null            — excluded from all nav derivation.
 *   title / navName: ''       — intentionally empty; not used at runtime.
 *   titleKey: null, headerOwner: null — no shell surface for a redirect.
 *   getSearchPagesByCapability() excludes redirect entries.
 */

/** @type {SearchPageEntry[]} */
export const SEARCH_PAGE_MAP = [
  {
    id: 'home',
    path: '/home',
    linkKey: 'home',
    menuTestId: 'menu-home',
    requiresCapability: null,
    legacyAliases: ['/search/home'],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'operator.shell.title',
    titleKey: 'operator.shell.title',
    headerOwner: 'shell',
    navGroup: 'top',
    navOrder: 1,
    navIcon: 'home',
    navName: 'sidebar.home',
  },
  {
    id: 'diagnostics',
    path: '/diagnostics',
    linkKey: 'diagnostics',
    menuTestId: 'menu-diagnostics',
    requiresCapability: null,
    legacyAliases: ['/search/diagnostics'],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.diagnostics.title',
    titleKey: 'search.page.diagnostics.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    navOrder: 99,
    navIcon: null,
    navName: 'sidebar.diagnostics',
  },
  {
    id: 'catalogHistory',
    path: '/search/catalog-history',
    linkKey: 'searchCatalogHistory',
    menuTestId: 'menu-catalog-history',
    requiresCapability: 'indexingV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.catalogHistory.title',
    titleKey: 'search.page.catalogHistory.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    navOrder: 4,
    cloudNavOrder: 3,
    navIcon: null,
    navName: 'sidebar.search.catalogHistory',
  },
  {
    id: 'settingsData',
    path: '/search/settings/data',
    linkKey: 'searchSettingsData',
    menuTestId: null,
    // Plugin compatibility alias. Cloud exposes the same legacy page through
    // the dedicated cloudDataConnectivity entry below.
    requiresCapability: null,
    legacyAliases: [],
    surface: { kind: 'redirect' },
    title: '',
    titleKey: null,
    headerOwner: null,
    navGroup: null,
    navOrder: 0,
    navIcon: null,
    navName: '',
  },
  {
    id: 'dataSettings',
    path: '/search/data-settings',
    linkKey: 'searchDataSettings',
    menuTestId: 'menu-data-settings',
    requiresCapability: 'catalogSelectorsV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.dataSettings.title',
    titleKey: 'search.page.dataSettings.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    navOrder: 6,
    navIcon: null,
    navName: 'sidebar.search.dataSettings',
    productIds: ['plugin'],
  },
  {
    id: 'descriptionParameters',
    path: '/search/settings/description-parameters',
    linkKey: 'searchDescriptionParameters',
    menuTestId: 'menu-description-parameters',
    requiresCapability: 'descriptionParametersV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'descriptionParameters.productTitle',
    titleKey: 'descriptionParameters.productTitle',
    headerOwner: 'shell',
    navGroup: 'settings',
    navOrder: 7,
    navIcon: null,
    navName: 'descriptionParameters.productTitle',
    productIds: ['plugin'],
  },
  {
    id: 'agentMcpSettings',
    path: '/search/settings/ai',
    linkKey: 'searchAgentMcpSettings',
    menuTestId: 'menu-agent-mcp-settings',
    requiresCapability: 'agentMcpAdminV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'agentMcp.page.title',
    titleKey: 'agentMcp.page.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    navOrder: 9,
    navIcon: null,
    navName: 'agentMcp.nav.title',
    productIds: ['plugin'],
  },
  {
    id: 'cloudDataConnectivity',
    path: '/search/settings/data-connectivity',
    linkKey: 'searchCloudData',
    menuTestId: 'menu-data-connectivity',
    requiresCapability: 'cloudDataSettingsV1',
    legacyAliases: ['/search/cloud-data'],
    surface: { kind: 'feature_page_shell', width: 'expanded' },
    title: 'search.page.dataConnectivity.title',
    titleKey: null,
    headerOwner: 'page',
    navGroup: 'settings',
    navOrder: 5,
    cloudNavOrder: 2,
    navIcon: null,
    navName: 'sidebar.search.settings.data',
    productIds: ['cloud'],
  },
  {
    id: 'settingsV2',
    path: '/search/settings/v2',
    linkKey: 'searchSettingsV2',
    menuTestId: 'menu-settings-v2',
    requiresCapability: 'settingsV2',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'fluid' },
    title: 'search.page.settingsV2.title',
    titleKey: 'search.page.settingsV2.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    cloudNavGroup: 'uiSettings',
    navOrder: 4,
    navIcon: null,
    navName: 'sidebar.search.uiSettings.v2',
    productIds: ['cloud', 'plugin'],
    hiddenInCloudNavigation: true,
  },
  {
    id: 'theme',
    path: '/search/settings/theme',
    linkKey: 'searchSettingsTheme',
    menuTestId: 'menu-config-theme',
    requiresCapability: 'settingsV2',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.theme.title',
    titleKey: 'search.page.theme.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    cloudNavGroup: 'uiSettings',
    navOrder: 5,
    navIcon: null,
    navName: 'sidebar.search.uiSettings.theme',
  },
  {
    id: 'searchBoxSettings',
    path: '/search/settings/searchbox',
    linkKey: 'searchSettingsSearchbox',
    menuTestId: 'menu-config-searchbox',
    requiresCapability: 'settingsV2',
    legacyAliases: [
      '/search/settings',
      '/search/settings/dropdown',
      '/search/settings/widget',
    ],
    surface: { kind: 'feature_page_shell', width: 'expanded' },
    title: 'search.page.searchBox.title',
    titleKey: 'search.page.searchBox.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    cloudNavGroup: 'uiSettings',
    navOrder: 6,
    navIcon: null,
    navName: 'sidebar.search.uiSettings.searchBox',
    requiredViews: ['searchBox', 'dropdown'],
  },
  {
    id: 'resultsPageSettings',
    path: '/search/settings/result-page',
    linkKey: 'searchSettingsResultPage',
    menuTestId: 'menu-config-results-page',
    requiresCapability: 'settingsV2',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'expanded' },
    title: 'search.page.resultsPage.title',
    titleKey: 'search.page.resultsPage.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    cloudNavGroup: 'uiSettings',
    navOrder: 7,
    navIcon: null,
    navName: 'sidebar.search.uiSettings.resultsPage',
    requiredViews: ['resultsPage'],
  },
  {
    id: 'searchMode',
    path: '/search/settings/search-mode',
    linkKey: 'searchSettingsSearchMode',
    menuTestId: 'menu-config-search-mode',
    requiresCapability: 'settingsV2',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.searchMode.title',
    titleKey: 'search.page.searchMode.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    cloudNavGroup: 'settings',
    navOrder: 1,
    navIcon: null,
    navName: 'sidebar.search.uiSettings.searchMode',
  },
  {
    id: 'storefrontContent',
    path: '/search/settings/storefront-content',
    linkKey: 'searchStorefrontContent',
    menuTestId: 'menu-storefront-content',
    requiresCapability: 'storefrontContentV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.storefrontContent.title',
    titleKey: 'search.page.storefrontContent.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    navOrder: 9,
    navIcon: null,
    navName: 'sidebar.search.uiSettings.storefrontContent',
    productIds: ['plugin'],
  },
  {
    id: 'customizationFields',
    path: '/search/customization/fields',
    linkKey: 'searchCustomizationFields',
    menuTestId: 'menu-config-fields',
    requiresCapability: 'searchCustomizationV1',
    legacyAliases: ['/search/customization'],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.customizationFields.title',
    titleKey: 'search.page.customizationFields.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    cloudNavGroup: 'customization',
    navOrder: 1,
    navIcon: null,
    navName: 'sidebar.search.settings.fields',
  },
  {
    id: 'customizationFilters',
    path: '/search/customization/filters',
    linkKey: 'searchCustomizationFilters',
    menuTestId: 'menu-config-facets',
    requiresCapability: 'searchCustomizationV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.customizationFilters.title',
    titleKey: 'search.page.customizationFilters.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    cloudNavGroup: 'customization',
    navOrder: 2,
    navIcon: null,
    navName: 'sidebar.search.settings.facets',
  },
  {
    id: 'customizationSynonyms',
    path: '/search/customization/synonyms',
    linkKey: 'searchCustomizationSynonyms',
    menuTestId: 'menu-config-synonyms',
    requiresCapability: 'searchCustomizationV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.customizationSynonyms.title',
    titleKey: 'search.page.customizationSynonyms.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    cloudNavGroup: 'customization',
    navOrder: 3,
    navIcon: null,
    navName: 'sidebar.search.settings.synonyms',
  },
  {
    id: 'customizationSuggestionOverrides',
    path: '/search/customization/suggestion-overrides',
    linkKey: 'searchCustomizationSuggestionOverrides',
    menuTestId: 'menu-config-suggestion-overrides',
    requiresCapability: 'suggestionOverridesV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.customizationSuggestionOverrides.title',
    titleKey: 'search.page.customizationSuggestionOverrides.title',
    headerOwner: 'shell',
    navGroup: 'settings',
    cloudNavGroup: 'customization',
    navOrder: 8,
    cloudNavOrder: 4,
    navIcon: null,
    navName: 'sidebar.search.settings.suggestionOverrides',
    productIds: ['cloud'],
    // The editor is intentionally still routable for development and saved
    // deep links, but is not ready to be advertised in production menus.
    hiddenInCloudNavigation: true,
  },
  {
    id: 'customizationTaxonomy',
    path: '/search/customization/taxonomy',
    linkKey: 'searchCustomizationTaxonomy',
    menuTestId: null,
    requiresCapability: 'searchCustomizationV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.customizationTaxonomy.title',
    titleKey: 'search.page.customizationTaxonomy.title',
    headerOwner: 'shell',
    navGroup: null,
    navOrder: 7,
    navIcon: null,
    navName: 'sidebar.search.settings.taxonomy',
    productIds: ['cloud'],
  },
  // ── Analytics pages ──────────────────────────────────────────────────────
  {
    id: 'analyticHealth',
    path: '/search/analytic/health',
    linkKey: 'searchAnalyticsHealth',
    menuTestId: 'menu-analytics-health',
    requiresCapability: 'analyticsV1',
    legacyAliases: ['/search/analytic', '/search/analytic/dashboard'],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.analyticHealth.title',
    titleKey: 'search.page.analyticHealth.title',
    headerOwner: 'shell',
    navGroup: 'analytics',
    navOrder: 0,
    navIcon: null,
    navName: 'sidebar.search.analytics.dashboard',
  },
  {
    id: 'analyticQueryPopular',
    path: '/search/analytic/query-popular',
    linkKey: 'searchQueryPopular',
    menuTestId: 'menu-analytics-top-queries',
    requiresCapability: 'analyticsV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.analyticQueryPopular.title',
    titleKey: 'search.page.analyticQueryPopular.title',
    headerOwner: 'shell',
    navGroup: 'analytics',
    navOrder: 1,
    navIcon: null,
    navName: 'sidebar.search.analytics.queryPopular',
  },
  {
    id: 'analyticQueryDetail',
    path: '/search/analytic/query/:query',
    linkKey: 'searchQueryDetail',
    menuTestId: null,
    requiresCapability: 'analyticsQueryDetailV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.analyticQueryDetail.title',
    titleKey: 'search.page.analyticQueryDetail.title',
    headerOwner: 'shell',
    navGroup: null,
    navOrder: 0,
    navIcon: null,
    navName: 'sidebar.search.analytics.queryDetail',
  },
  {
    id: 'analyticQueryNoResult',
    path: '/search/analytic/query-no-result',
    linkKey: 'searchQueryNoResult',
    menuTestId: 'menu-analytics-zero-results',
    requiresCapability: 'analyticsV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.analyticQueryNoResult.title',
    titleKey: 'search.page.analyticQueryNoResult.title',
    headerOwner: 'shell',
    navGroup: 'analytics',
    navOrder: 2,
    navIcon: null,
    navName: 'sidebar.search.analytics.queryNoResult',
  },
  {
    id: 'analyticLostOpportunities',
    path: '/search/analytic/lost-opportunities',
    linkKey: 'searchLostOpportunities',
    menuTestId: 'menu-analytics-lost-opportunities',
    requiresCapability: 'analyticsV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.analyticLostOpportunities.title',
    titleKey: 'search.page.analyticLostOpportunities.title',
    headerOwner: 'shell',
    navGroup: 'analytics',
    navOrder: 3,
    navIcon: null,
    navName: 'sidebar.search.analytics.lostOpportunities',
  },
  {
    id: 'analyticPopularProducts',
    path: '/search/analytic/popular-products',
    linkKey: 'searchAnalyticsPopularProducts',
    menuTestId: 'menu-analytics-popular-products',
    requiresCapability: 'analyticsPopularProductsV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.analyticPopularProducts.title',
    titleKey: 'search.page.analyticPopularProducts.title',
    headerOwner: 'shell',
    navGroup: 'analytics',
    navOrder: 4,
    navIcon: null,
    navName: 'sidebar.search.analytics.popularProducts',
  },
  // ── Customization: empty-query ───────────────────────────────────────────
  {
    id: 'customizationEmptyQuery',
    path: '/search/customization/empty-query',
    linkKey: 'searchCustomizationEmptyQuery',
    menuTestId: 'menu-config-empty-query',
    requiresCapability: 'emptyQueryV1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'empty.query.page.title',
    titleKey: 'empty.query.page.title',
    headerOwner: 'shell',
    navGroup: 'merchandising',
    navOrder: 1,
    navIcon: null,
    navName: 'sidebar.search.settings.emptyQuery',
  },
  {
    id: 'merchandisingSearchRules',
    path: '/search/merchandising/rules/query',
    linkKey: 'searchMerchandisingQueryRules',
    menuTestId: 'menu-merchandising-rules',
    requiresCapability: 'merchandising_bridge_v1',
    legacyAliases: ['/search/merchandising/rules'],
    surface: { kind: 'feature_page_shell', width: 'fluid' },
    title: 'merchandising.workbench.searchRules',
    titleKey: null,
    headerOwner: 'page',
    navGroup: 'merchandising',
    navOrder: 2,
    navIcon: null,
    navName: 'sidebar.search.merchandising.searchRules',
  },
  {
    id: 'merchandisingProductRanking',
    path: '/search/merchandising/rules/products',
    linkKey: 'searchMerchandisingProductRanking',
    menuTestId: 'menu-merchandising-product-ranking',
    requiresCapability: 'merchandising_bridge_v1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'fluid' },
    title: 'merchandising.workbench.productRanking',
    titleKey: null,
    headerOwner: 'page',
    navGroup: 'merchandising',
    navOrder: 3,
    navIcon: null,
    navName: 'sidebar.search.merchandising.productRanking',
  },
  {
    id: 'merchandisingCategoryPriorities',
    path: '/search/merchandising/rules/categories',
    linkKey: 'searchMerchandisingCategoryPriorities',
    menuTestId: 'menu-merchandising-category-priorities',
    requiresCapability: 'merchandising_bridge_v1',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'fluid' },
    title: 'merchandising.workbench.categoryPriorities',
    titleKey: null,
    headerOwner: 'page',
    navGroup: 'merchandising',
    navOrder: 4,
    navIcon: null,
    navName: 'sidebar.search.merchandising.categoryPriorities',
  },
  {
    id: 'cardTemplate',
    path: '/search/customization/card-template',
    linkKey: 'searchCustomizationCardTemplate',
    menuTestId: 'menu-config-card-template',
    requiresCapability: 'cardTemplateEditor',
    legacyAliases: [],
    surface: { kind: 'feature_page_shell', width: 'wide' },
    title: 'search.page.cardTemplate.title',
    titleKey: 'search.page.cardTemplate.title',
    headerOwner: 'shell',
    navGroup: 'uiSettings',
    navOrder: 8,
    navIcon: null,
    navName: 'sidebar.search.settings.cardTemplate',
  },
];

/**
 * Convenience: find a page entry by id.
 * @param {string} id
 * @returns {SearchPageEntry|undefined}
 */
export function getSearchPage(id) {
  return SEARCH_PAGE_MAP.find((p) => p.id === id);
}

/**
 * Convenience: return entries that require the given capability.
 * Redirect aliases (surface.kind === 'redirect') are excluded — they do not
 * represent real capability-gated pages.
 * @param {string} capability
 * @returns {SearchPageEntry[]}
 */
export function getSearchPagesByCapability(capability) {
  return SEARCH_PAGE_MAP.filter(
    (p) => p.requiresCapability === capability && p.surface.kind !== 'redirect',
  );
}

/**
 * Metadata for product-admin nav collapse groups, keyed by navGroup.
 *
 * @type {Record<string, { name: string, icon: string, testId: string, capabilityKey: string|null }>}
 */
export const PLUGIN_ADMIN_NAV_GROUPS = {
  analytics: {
    name: 'sidebar.search.analytics',
    icon: 'chart',
    testId: 'menu-collapse-analytics',
    capabilityKey: 'operatorStateV1',
  },
  settings: {
    name: 'sidebar.search.settings',
    icon: 'settings',
    testId: 'menu-collapse-settings',
    capabilityKey: 'operatorStateV1',
  },
  merchandising: {
    name: 'sidebar.search.merchandising',
    icon: 'code-setting',
    testId: 'menu-collapse-merchandising',
    capabilityKey: null,
  },
  uiSettings: {
    name: 'sidebar.search.uiSettings',
    icon: 'palette',
    testId: 'menu-collapse-ui-settings',
    capabilityKey: 'operatorStateV1',
  },
  customization: {
    name: 'sidebar.search.customization',
    icon: 'tune',
    testId: 'menu-collapse-customization',
    capabilityKey: 'operatorStateV1',
  },
};

/**
 * Returns product-admin nav entries grouped by navGroup, sorted by navOrder.
 * Entries with navGroup === null are excluded.
 *
 * @returns {{
 *   top: SearchPageEntry[],
 *   analytics: SearchPageEntry[],
 *   settings: SearchPageEntry[],
 *   merchandising: SearchPageEntry[],
 *   uiSettings: SearchPageEntry[],
 *   customization: SearchPageEntry[],
 * }}
 */
export function getPluginAdminNavEntries(productId = 'plugin') {
  const top = [];
  const analytics = [];
  const settings = [];
  const merchandising = [];
  const uiSettings = [];
  const customization = [];

  for (const entry of SEARCH_PAGE_MAP) {
    // Redirect aliases are not navigable pages — exclude from all nav groups
    if (entry.surface.kind === 'redirect') continue;
    if (Array.isArray(entry.productIds) && !entry.productIds.includes(productId)) continue;
    if (productId === 'cloud' && entry.hiddenInCloudNavigation) continue;
    const effectiveNavGroup = productId === 'cloud'
      ? (entry.cloudNavGroup || entry.navGroup)
      : entry.navGroup;
    const productEntry = effectiveNavGroup === entry.navGroup
      && !(productId === 'cloud' && Number.isFinite(entry.cloudNavOrder))
      ? entry
      : {
        ...entry,
        navGroup: effectiveNavGroup,
        navOrder: productId === 'cloud' && Number.isFinite(entry.cloudNavOrder)
          ? entry.cloudNavOrder
          : entry.navOrder,
      };
    if (effectiveNavGroup === 'top') top.push(productEntry);
    else if (effectiveNavGroup === 'analytics') analytics.push(productEntry);
    else if (effectiveNavGroup === 'settings') settings.push(productEntry);
    else if (effectiveNavGroup === 'merchandising') merchandising.push(productEntry);
    else if (effectiveNavGroup === 'uiSettings') uiSettings.push(productEntry);
    else if (effectiveNavGroup === 'customization') customization.push(productEntry);
  }

  const byOrder = (a, b) => a.navOrder - b.navOrder;
  return {
    top: top.sort(byOrder),
    analytics: analytics.sort(byOrder),
    settings: settings.sort(byOrder),
    merchandising: merchandising.sort(byOrder),
    uiSettings: uiSettings.sort(byOrder),
    customization: customization.sort(byOrder),
  };
}

/**
 * Returns the page-shell surface contract (title + width) for a canonical
 * search page entry.
 *
 * Throws explicitly for invalid usage:
 *   - unknown id
 *   - redirect alias (surface.kind === 'redirect')
 *   - non-feature_page_shell entry
 *
 * Routed search/admin pages that render under FeaturePageShell can obtain
 * centralized shell metadata through this function. The current rollout uses
 * it as the runtime source of truth for width, and for title where page-local
 * header ownership / localization does not intentionally override the map.
 *
 * @param {string} id  Canonical searchPageMap entry id.
 * @returns {{ titleKey: string|null, width: 'default'|'wide'|'expanded'|'fluid', headerOwner: 'shell'|'page' }}
 */
export function getSearchPageSurface(id) {
  const entry = SEARCH_PAGE_MAP.find((p) => p.id === id);

  if (!entry) {
    throw new Error(
      `getSearchPageSurface: unknown page id "${id}". ` +
      `Valid ids: ${SEARCH_PAGE_MAP.map((p) => p.id).join(', ')}`,
    );
  }

  if (entry.surface.kind === 'redirect') {
    throw new Error(
      `getSearchPageSurface: "${id}" is a redirect alias, not a real page. ` +
      `Redirect entries have no shell surface contract.`,
    );
  }

  if (entry.surface.kind !== 'feature_page_shell') {
    throw new Error(
      `getSearchPageSurface: "${id}" has surface.kind="${entry.surface.kind}", ` +
      `not "feature_page_shell". Only feature_page_shell pages have a shell surface contract.`,
    );
  }

  return { titleKey: entry.titleKey, width: entry.surface.width, headerOwner: entry.headerOwner };
}
